function readEnv(key: string): string | undefined {
  try {
    const metaEnv = (import.meta.env as Record<string, unknown> | undefined)?.[key];
    if (typeof metaEnv === 'string' && metaEnv.trim().length > 0) {
      return metaEnv.trim();
    }
  } catch {
    // ignore
  }

  try {
    if (typeof process !== 'undefined' && process.env?.[key]) {
      const val = process.env[key];
      if (typeof val === 'string' && val.trim().length > 0) {
        return val.trim();
      }
    }
  } catch {
    // ignore
  }

  return undefined;
}

function getBrowserLocation(): Location | undefined {
  if (typeof window !== 'undefined' && window.location) {
    return window.location;
  }
  const g = globalThis as any;
  if (g && g.window && g.window.location) {
    return g.window.location;
  }
  return undefined;
}

/**
 * Resuelve la URL base de la API backend de forma determinista y resiliente:
 * 1. Si VITE_API_URL está definida en tiempo de compilación o ejecución, usarla.
 * 2. Si el código corre en navegador:
 *    - Si el hostname es iridiachat.com o www.iridiachat.com -> https://api.iridiachat.com
 *    - Si es cualquier otro host de producción que no sea localhost -> origen actual
 * 3. Fallback para desarrollo local -> http://localhost:3001
 */
export function getApiBaseUrl(): string {
  const envUrl = readEnv('VITE_API_URL');
  const loc = getBrowserLocation();

  // Si estamos en un dominio de producción en el navegador (ej: iridiachat.com),
  // y envUrl no está configurada o apunta accidentalmente a localhost, priorizar el dominio canónico.
  if (loc) {
    const hostname = loc.hostname || '';
    const protocol = loc.protocol || 'https:';
    if (hostname === 'iridiachat.com' || hostname === 'www.iridiachat.com') {
      if (!envUrl || envUrl.includes('localhost')) {
        return `${protocol}//api.iridiachat.com`;
      }
    }
    if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.endsWith('.local')) {
      if (!envUrl || envUrl.includes('localhost')) {
        const origin = loc.origin || `${protocol}//${hostname}`;
        return origin.replace(/\/+$/, '');
      }
    }
  }

  if (envUrl) {
    return envUrl.replace(/\/+$/, '');
  }

  return 'http://localhost:3001';
}

/**
 * Resuelve la URL base de WebSockets (WSS/WS) para Socket.IO y telemetría en tiempo real.
 */
export function getWsBaseUrl(): string {
  const apiBase = getApiBaseUrl();
  const envWs = readEnv('VITE_WS_URL');

  // Solo usar envWs si no es un residuo de localhost cuando la API objetivo es remota
  if (envWs && (!envWs.includes('localhost') || apiBase.includes('localhost'))) {
    return envWs.replace(/\/+$/, '');
  }

  if (apiBase.startsWith('https://')) {
    return apiBase.replace('https://', 'wss://');
  }
  if (apiBase.startsWith('http://')) {
    return apiBase.replace('http://', 'ws://');
  }

  return 'ws://localhost:3001';
}
